'use client';

import { useEffect, useRef, useState } from 'react';
import s from '@/components/ui/Navbar/Navbar.module.css';
import { Ellipsis, Pencil, Plus, Trash } from 'lucide-react';
import Button from '@/components/ui/Button';
import { ToneAndChildren, toTonePathname } from '@/utils/helpers';
import { useRouter } from 'next/navigation';
import { ActionModal } from '../../ActionModal/ActionModal';
import LoadingDots from '../../LoadingDots';
import Link from 'next/link';
import { FORMAT } from '@/utils/constants';
interface ToneMenuProps {
  tone: ToneAndChildren;
}

export const ToneMenu = ({ tone }: ToneMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const router = useRouter();
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: { target: any }) => {
      // @ts-ignore
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onDelete = async (id: number) => {
    setIsDeleting(true);
    const res = await fetch('/api/tones/delete', {
      method: 'POST',
      body: JSON.stringify({ toneId: id }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (res.ok) {
      router.push(`/${tone.user.username}`);
      router.refresh();
    }
    setIsDeleting(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant={'slim'}
        isSecondary={true}
        style={{ paddingLeft: '16px', paddingRight: '16px' }}
      >
        <Ellipsis size={20} />
      </Button>

      <nav
        className={`${isOpen ? 'block' : 'hidden'} absolute z-50 right-0 top-14 bg-black text-white rounded-lg shadow-lg border border-zinc-500 border-opacity-50 min-w-[192px] bg-zinc-900`}
      >
        <div className="flex flex-col p-3">
          <Link
            href={
              tone.platform === FORMAT.NAM
                ? `/create/add?toneId=${tone.id}`
                : `/create/upload?toneId=${tone.id}`
            }
            className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
          >
            <Plus size={20} className="flex-shrink-0" />
            Add Models
          </Link>
          <Link
            href={`/tones/${toTonePathname(tone)}/edit`}
            className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
          >
            <Pencil size={20} className="flex-shrink-0" />
            Edit
          </Link>
          <ActionModal
            trigger={
              <span
                className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
              >
                <Trash size={20} className="flex-shrink-0" />
                Delete
              </span>
            }
            title={'Delete Tone'}
            message={`Are you sure you want to delete "${tone.title}"? This will permanently delete all models linked to this tone and cannot be undone.`}
            primaryAction={
              <Button
                className={'w-full text-white'}
                style={{ background: 'red', color: 'white' }}
                onClick={() => onDelete(tone.id!)}
                variant={'slim'}
                tracking={{
                  event: 'tone.deleted',
                }}
              >
                {isDeleting && <LoadingDots />}
                Delete
              </Button>
            }
            cancelText={'Cancel'}
          />
        </div>
      </nav>
    </div>
  );
};
